import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import { LocationConsentDialog } from '@/components/location/LocationConsentDialog'
import {
  getLocationStatusText,
  useBrowserTerritoryLocation,
  type BrowserLocationStatus,
} from '@/hooks/useBrowserTerritoryLocation'
import type { InferredTerritory } from '@/lib/data/territoryLocation'

const TERRITORY_CACHE_KEY = 'ee26_territory_session'
const LOCATION_DISMISSED_KEY = 'ee26_location_dismissed'

interface TerritoryLocationContextValue {
  detectedTerritory: InferredTerritory | null
  locationStatus: BrowserLocationStatus
  locationStatusText: string
  requestLocation: (forcePrompt?: boolean) => void
}

const TerritoryLocationContext = createContext<TerritoryLocationContextValue | null>(null)

function readCachedTerritory() {
  try {
    if (typeof window === 'undefined') return null
    const value = sessionStorage.getItem(TERRITORY_CACHE_KEY)
    return value ? (JSON.parse(value) as InferredTerritory) : null
  } catch {
    return null
  }
}

function readLocationDismissed() {
  try {
    return sessionStorage.getItem(LOCATION_DISMISSED_KEY) === 'true'
  } catch {
    return false
  }
}

function writeSessionValue(key: string, value?: string) {
  try {
    if (value === undefined) sessionStorage.removeItem(key)
    else sessionStorage.setItem(key, value)
  } catch {
    // La ubicación sigue funcionando aunque el navegador bloquee sessionStorage.
  }
}

export function TerritoryLocationProvider({ children }: PropsWithChildren) {
  const [cachedTerritory, setCachedTerritory] = useState<InferredTerritory | null>(
    readCachedTerritory,
  )
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const {
    detectedTerritory: browserTerritory,
    locationStatus: browserLocationStatus,
    requestLocation: requestBrowserLocation,
  } = useBrowserTerritoryLocation(false)
  const detectedTerritory = browserTerritory ?? cachedTerritory
  const locationStatus: BrowserLocationStatus = detectedTerritory
    ? 'resolved'
    : browserLocationStatus

  useEffect(() => {
    if (!browserTerritory) return

    setCachedTerritory(browserTerritory)
    writeSessionValue(TERRITORY_CACHE_KEY, JSON.stringify(browserTerritory))
  }, [browserTerritory])

  const requestLocation = useCallback(
    (forcePrompt = false) => {
      if (detectedTerritory || browserLocationStatus === 'locating') return
      if (!forcePrompt && browserLocationStatus !== 'idle') return
      if (!forcePrompt && readLocationDismissed()) return
      setIsDialogOpen(true)
    },
    [browserLocationStatus, detectedTerritory],
  )

  const closeDialog = useCallback(() => {
    writeSessionValue(LOCATION_DISMISSED_KEY, 'true')
    setIsDialogOpen(false)
  }, [])

  const acceptLocation = useCallback(() => {
    writeSessionValue(LOCATION_DISMISSED_KEY)
    setIsDialogOpen(false)
    requestBrowserLocation()
  }, [requestBrowserLocation])

  const value = useMemo<TerritoryLocationContextValue>(
    () => ({
      detectedTerritory,
      locationStatus,
      locationStatusText: getLocationStatusText(locationStatus),
      requestLocation,
    }),
    [detectedTerritory, locationStatus, requestLocation],
  )

  return (
    <TerritoryLocationContext.Provider value={value}>
      {children}
      <LocationConsentDialog
        isOpen={isDialogOpen}
        onAccept={acceptLocation}
        onClose={closeDialog}
      />
    </TerritoryLocationContext.Provider>
  )
}

export function useTerritoryLocation() {
  const context = useContext(TerritoryLocationContext)

  if (!context) {
    throw new Error('useTerritoryLocation debe usarse dentro de TerritoryLocationProvider')
  }

  return context
}
